import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback, useRef } from "react";
// Components
import IconWrapper from "@vahak/core-ui/dist/components/IconWrapper";
import CallPopup from "../../components/call/CallPopup";
import { toast } from "@vahak/core/dist/components/toast/toast";
// Constants
import { AppContext } from "@vahak/core/dist/app-context";
import { CallInfo as _CallInfo } from "plivo-browser-sdk/managers/callSession";
// Methods
import { extractPhoneNumber } from "@vahak/core-ui/dist/methods/string";
// Hooks
import { CallInitiationParams, hasSingleCallCalleeId, useCallInitiation } from "../../services/call/useCallInitiation";
import { useSharedPlivoSdk } from "./SharedPlivoSdkProvider";
// Assets
import CallIconMobile from "@vahak/core/dist/icons/Call_CTA_BLUE.svg";
import { useSearchCompanyByPhoneNumberMutation } from "@vahak/core/dist/_services/auth.adminConsole.service";
import { PLVIO_CALL_STATE } from "./type";
import { playCallTerminatedTone } from "@vahak/core-ui/dist/methods";
// =============================== * =============================== * =============================== //

interface CallPopupContextType {
    startCall: (meta: CallRequiredMeta, callInitiateParams: CallInitiationParams) => void;
    registerCallCta: (meta: CallRequiredMeta, callInitiateParams: CallInitiationParams) => void;
    isReady?: boolean;
    isCallSessionActive?: boolean;
    callCtaStateKey?: string;
}

// ✅ Create Context
const CallPopupContext = createContext<CallPopupContextType | undefined>(undefined);
interface CallRequiredMeta {
    name: string;
    number: string;
    imgUrl: string;
}

export interface CallInfo extends Partial<_CallInfo> {
    state?: PLVIO_CALL_STATE;
    callDesc?: string;
    popupActive?: boolean; // show UI
    isMuted?: boolean;
    startTime?: Date;
    endTime?: Date;
    calleInfomation?: CallRequiredMeta;
    callInitiateParams?: CallInitiationParams;
    //
    isOutgoingCall?: boolean;
    isIncomingCall?: boolean;
    isCallActive?: boolean; // Ui Maybe visible but call is really active and actionable
    isConnected?: boolean; // action taken and call is now connected
}

export const SingleClickToCallProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { companyId, route } = useContext(AppContext);
    const { plivoBrowserSdk, isReady } = useSharedPlivoSdk();
    const [callInfo, setCallInfo] = useState({} as CallInfo);
    const getPhoneNumberDetails = useSearchCompanyByPhoneNumberMutation();
    const callIntitate = useCallInitiation();

    const updateCallInfo = (data: Partial<CallInfo>) => setCallInfo((v) => ({ ...v, ...data }));

    // CTA
    const toggleMute = () => {
        setCallInfo((v) => ({ ...v, isMuted: !v.isMuted }));
        callInfo?.isMuted ? plivoBrowserSdk?.client?.unmute() : plivoBrowserSdk?.client?.mute();
    };

    const onHangup = () => {
        try {
            plivoBrowserSdk?.client?.hangup();
            // onCallTerminated will get triggered
        } catch (error) {
            console.error("onHangup ~ error:", error);
        }
    };
    const onReject = () => {
        plivoBrowserSdk?.client?.reject("");
        // onCallFailed will get triggered
    };
    const onAnswer = () => {
        if (callInfo?.callUUID) {
            plivoBrowserSdk?.client?.answer(callInfo?.callUUID, "reject");
            //onCallAnswered
        }
    };

    const updateUserDetails = (phone_number: string) => {
        const reqNumber = extractPhoneNumber(phone_number || "");
        if (reqNumber) {
            getPhoneNumberDetails.mutate(
                {
                    phoneNumber: reqNumber
                },
                {
                    onError: () => {},
                    onSuccess: (res) => {
                        if (res?.data?.length !== 0) {
                            const user: any = res?.data?.at(0);
                            updateCallInfo({
                                calleInfomation: {
                                    imgUrl: user?.logo,
                                    name: user?.name,
                                    number: phone_number
                                }
                            });
                        }
                    }
                }
            );
        }
    };

    // Helper

    const startCall: CallPopupContextType["startCall"] = (meta, callInitiateParams) => {
        if (callInfo?.isCallActive || callInfo?.popupActive) {
            return;
        }

        const payload: CallInitiationParams = {
            ...callInitiateParams,
            caller_id: callInitiateParams?.caller_id || Number(companyId)
        };

        return new Promise((resolve) => {
            const { number } = meta || {};
            const reqNumber = extractPhoneNumber(number || "");
            callIntitate?.mutateAsync(payload, {
                onSuccess: () => {
                    if (reqNumber && plivoBrowserSdk?.client?.isLoggedIn) {
                        setCallInfo({
                            calleInfomation: meta,
                            callInitiateParams: payload,
                            popupActive: true,
                            isOutgoingCall: true,
                            isCallActive: true,
                            callDesc: "Connecting"
                        });
                        plivoBrowserSdk?.client.call(`91${reqNumber}`, {
                            ...(payload.reference_id != null && {
                                "X-PH-reference-id": String(payload.reference_id)
                            }),
                            ...(payload.reference_type != null && {
                                "X-PH-reference-type": String(payload.reference_type)
                            }),
                            ...(payload.caller_id != null && {
                                "X-PH-caller-id": String(payload.caller_id)
                            }),
                            ...(payload.callee_id != null && {
                                "X-PH-callee-id": String(payload.callee_id)
                            }),
                            ...(payload.load_id != null && {
                                "X-PH-load-id": String(payload.load_id)
                            }),
                            ...(payload.lorry_id != null && {
                                "X-PH-lorry-id": String(payload.lorry_id)
                            }),
                            ...(payload.bid_id != null && {
                                "X-PH-bid-id": String(payload.bid_id)
                            }),
                            ...(payload.ticket_id != null && {
                                "X-PH-ticket-id": String(payload.ticket_id)
                            }),
                            ...(route && { "X-PH-screen-name": route })
                        });
                        resolve(0);
                    }
                },
                onError: (err) => {
                    err?.message && toast.error(err?.message);
                }
            });
        });
    };

    const reset = (delay: number = 2000) => {
        setTimeout(() => {
            setCallInfo({ popupActive: false, isCallActive: false, isConnected: false });
        }, delay);
    };

    const Events = {
        // Outbound Start
        onCalling: (...arr: any) => {
            console.error("onCalling ~ arr:", arr);
            updateCallInfo({
                callDesc: "Connecting",
                popupActive: true,
                isOutgoingCall: true,
                isCallActive: true
            });
        },
        onCallRemoteRinging: (_callInfo: CallInfo) => {
            console.error("onCallRemoteRinging ~ arr:", { _callInfo });
            updateCallInfo({
                ..._callInfo,
                callDesc: "Ringing"
            });
        },
        onCallConnected: (_callInfo: CallInfo) => {
            console.error("onCallConnected ~ arr:", _callInfo);
            updateCallInfo({
                ..._callInfo,
                callDesc: "Connected"
            });
        },
        onCallFailed: (reason: string, _callInfo: CallInfo) => {
            console.error("onCallFailed ", { reason, _callInfo });
            updateCallInfo({
                ..._callInfo,
                callDesc: reason,
                isCallActive: false,
                isConnected: false
            });
            reset();
        },
        onCallAnswered: (_callInfo: CallInfo) => {
            console.error("onCallAnswered ~ arr:", _callInfo);
            updateCallInfo({
                ..._callInfo,
                callDesc: "Answered",
                isConnected: true,
                startTime: new Date()
            });
        },
        onCallTerminated: (hangUpInfo: any, _callInfo: CallInfo) => {
            console.error("onCallTerminated ~ arr:", { hangUpInfo, _callInfo, callInfo });
            updateCallInfo({
                callDesc: "Call Ended",
                isCallActive: false,
                isConnected: false,
                endTime: new Date()
            });
            reset();
            playCallTerminatedTone(2, 200);
        },
        onIncomingCall: (callerID: string, extraHeaders: any, _callInfo: CallInfo, callerName: string) => {
            console.error("onIncomingCall ~ arr:", { callerID, extraHeaders, _callInfo, callerName });
            updateCallInfo({
                ..._callInfo,
                callDesc: "Incoming Call",
                popupActive: true,
                isCallActive: true,
                isIncomingCall: true,
                calleInfomation: {
                    imgUrl: "",
                    name: "",
                    number: callerName
                }
            });
            updateUserDetails(callerName);
        },
        onIncomingCallCanceled: (...arr: any) => {
            console.error("onIncomingCallCanceled ~ arr:", arr);
            updateCallInfo({
                callDesc: "Incoming Call Cancelled By User",
                isCallActive: false,
                isConnected: false
            });
            reset();
        }
    };

    useEffect(() => {
        if (!plivoBrowserSdk) {
            return;
        }

        const client = plivoBrowserSdk.client;
        const callEvents: Array<[string, (...args: any[]) => void]> = [
            ["onCalling", Events.onCalling],
            ["onCallConnected", Events.onCallConnected],
            ["onCallAnswered", Events.onCallAnswered],
            ["onCallRemoteRinging", Events.onCallRemoteRinging],
            ["onCallFailed", Events.onCallFailed],
            ["onCallTerminated", Events.onCallTerminated],
            ["onIncomingCall", Events.onIncomingCall],
            ["onIncomingCallCanceled", Events.onIncomingCallCanceled]
        ];

        callEvents.forEach(([event, handler]) => client.on(event, handler));

        return () => {
            callEvents.forEach(([event, handler]) => client.off(event, handler));
        };
    }, [plivoBrowserSdk, companyId]);

    const isCallSessionActive = Boolean(callInfo?.isCallActive || callInfo?.popupActive);

    const registerCallCta: CallPopupContextType["registerCallCta"] = (meta, callInitiateParams) => {
        if (isCallSessionActive) {
            return null;
        }

        if (isReady && meta?.number && hasSingleCallCalleeId(callInitiateParams)) {
            return (
                <IconWrapper onClick={() => startCall(meta, callInitiateParams)}>
                    <CallIconMobile />
                </IconWrapper>
            );
        }

        return null;
    };

    const showCallAcceptAction = callInfo?.isCallActive && callInfo?.isIncomingCall && !callInfo?.isConnected;
    const showCallHanguptAction = callInfo?.isIncomingCall ? callInfo?.isConnected : callInfo?.isCallActive;
    const showCallRejectAction = callInfo?.isIncomingCall && callInfo?.isCallActive && !callInfo?.isConnected;

    const startCallRef = useRef(startCall);
    const registerCallCtaRef = useRef(registerCallCta);
    startCallRef.current = startCall;
    registerCallCtaRef.current = registerCallCta;

    const stableStartCall = useCallback<CallPopupContextType["startCall"]>(
        (meta, callInitiateParams) => startCallRef.current(meta, callInitiateParams),
        []
    );
    const stableRegisterCallCta = useCallback<CallPopupContextType["registerCallCta"]>(
        (meta, callInitiateParams) => registerCallCtaRef.current(meta, callInitiateParams),
        []
    );

    const contextValue = useMemo(
        () => ({
            startCall: stableStartCall,
            registerCallCta: stableRegisterCallCta,
            isReady,
            isCallSessionActive,
            callCtaStateKey: String(isCallSessionActive)
        }),
        [isReady, isCallSessionActive, stableStartCall, stableRegisterCallCta]
    );

    return (
        <>
            {isReady && !!callInfo?.popupActive && (
                <CallPopup
                    onToggleMute={toggleMute}
                    callInfo={callInfo}
                    {...(showCallAcceptAction && {
                        onAnswer
                    })}
                    {...(showCallHanguptAction && {
                        onHangup
                    })}
                    {...(showCallRejectAction && {
                        onReject
                    })}
                />
            )}
            <CallPopupContext.Provider value={contextValue}>{children}</CallPopupContext.Provider>
        </>
    );
};

// ✅ Custom Hook for Using Context
export const useSingleClickToCall = (): CallPopupContextType | undefined => {
    const context = useContext(CallPopupContext);
    return context;
};
